import { useCallback } from 'react'
import { FormikHelpers } from 'formik'
import { toast } from '@sales-center/components'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import {
  ENTITY_TYPES,
  isAPIValidationError,
  updateLoadingState,
} from '@sales-center/common'

import { useAppDispatch } from '@user/store/hooks'
import { createOpportunity } from '@user/store/opportunity'
import { useSelectCustomFieldData } from '@user/store/customFields'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'

import { FormToPostModel } from '../formik'
import type { IAddOpportunityFormModel } from '../typings'
import { selectAddOpportunitySlice } from '@user/store/opportunity/addOpportunity/addOpportunitySlice'
import { useRemoveHiddenFieldsFromAPI } from '@user/components/Opportunities/AddOpportunityPage/hooks/removeHiddenFieldsFromAPI'
import { useGetEntityFormDataOnMount } from '@user/components/Settings/EntityFormSettings'

type SubmitHandler = (
  model: IAddOpportunityFormModel,
  helpers: FormikHelpers<IAddOpportunityFormModel>,
) => Promise<void>

type Props = {
  leadId?: number
  entityType: ENTITY_TYPES
  isOppFromOpp?: boolean
}

export function useOnSubmit(props: Props) {
  const { leadId, entityType, isOppFromOpp } = props

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const products = useSelector(selectAllLeadOppProducts)
  const { customFields } = useSelectCustomFieldData()
  const { billTo, serviceLocations } = useSelector(selectAddOpportunitySlice)
  const { entitySettingsMap, customFieldSettings } =
    useGetEntityFormDataOnMount(ENTITY_TYPES.Opportunity)

  const { removeHiddenFieldsFromAPI } = useRemoveHiddenFieldsFromAPI(
    entitySettingsMap,
    customFieldSettings,
  )
  const onSubmit = useCallback<SubmitHandler>(
    async (model, helpers) => {
      const { externalLinkId: billToExternalLinkId } = billTo
      dispatch(updateLoadingState('visible'))
      const postModel = FormToPostModel(
        model,
        entityType,
        customFields,
        leadId,
        billToExternalLinkId?.toString() || '',
        products,
        serviceLocations,
        isOppFromOpp,
      )
      const cleanedOppData = removeHiddenFieldsFromAPI(postModel)
      const result = await dispatch(createOpportunity(cleanedOppData))
      dispatch(updateLoadingState('hidden'))

      if (createOpportunity.fulfilled.match(result)) {
        toast.success('Opportunity created')

        const baseUrl = '/opportunities'
        if (model.viewDetail) {
          navigate(`${baseUrl}/${result.payload.id}`)
        } else {
          navigate(baseUrl)
        }
      } else {
        const { payload } = result

        if (!!payload) {
          if (isAPIValidationError(payload)) {
            const { errors } = payload
            if (errors) {
              if (errors['Name']) {
                helpers.setFieldError(
                  'opportunityName',
                  errors['Name'].join(', '),
                )
              }
              if (errors['Products']) {
                helpers.setFieldError('products', errors['Products'].join(', '))
              }

              if (errors['SalesFunnelStageId']) {
                helpers.setFieldError(
                  'opportunityStageId',
                  errors['SalesFunnelStageId'].join(', '),
                )
              }
            }
          } else {
            toast.error(payload.Message)
          }
        }
      }
    },
    [
      billTo,
      entityType,
      customFields,
      leadId,
      products,
      serviceLocations,
      isOppFromOpp,
      dispatch,
      navigate,
    ],
  )

  return onSubmit
}
